import { useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { Box, Chip, Link } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../../../sporttia/AppContext';
import {
    SttButtonFab,
    SttTopControls,
    SttTable,
    SttPage,
} from '../../../sporttia/all';
import { useToggle } from '../../../lib/hooks';
import Rate from './Rate';
import { formatPriceByLocale } from '../../../lib/utils';
import { useRatesTour } from 'src/lib/tours/useRatesTour';
import { PERMISSIONS } from '../../../lib/permissions';

type RateGroup = {
    id: number;
    name: string;
};

type RatePrice = {
    price: number;
    duration: number;
};

type RateItem = {
    id: number;
    name: string;
    individual: boolean;
    prices: RatePrice[];
    groups?: RateGroup[];
    trash: number;
};

type RatesResponse = {
    rows: RateItem[];
    count: number;
};

type RateChosen = {
    id: string | number | null;
    trash: number | null;
};

export default function Rates(): React.ReactElement {
    const { t } = useTranslation();
    const cxt = useContext(AppContext)!;
    const { startTour } = useRatesTour();

    useEffect(() => {
        cxt?.setHeader({ title: t('Rates') });
    }, []);

    const [rates, setRates] = useState<RatesResponse | null>(null);
    const [filters, setFilters] = useState<{ name: string }>({ name: '' });

    const [trash, toggleTrash] = useToggle(() => {
        loadRates();
    });

    const [rateChosen, setRateChosen] = useState<RateChosen>({
        id: null,
        trash: null,
    });

    const loadRates = useCallback(
        (params = { page: 1, rows: 20 }) => {
            cxt?.api('GET', `/scs/${cxt?.sc?.id}/rates`, {
                params: {
                    ...params,
                    name: filters.name,
                    trash,
                },
                success: (response: RatesResponse) => {
                    setRates(response);
                },
            });
        },
        [cxt, filters.name, trash],
    );

    const handleFilterChange = useCallback(
        ({ name, value }: { name: string | undefined; value: unknown }) => {
            if (!name) return;
            setFilters((prev) => ({ ...prev, [name]: value }));
        },
        [],
    );

    const openRate = useCallback((rate: RateItem) => {
        setRateChosen({ id: rate.id, trash: rate.trash });
    }, []);

    const closeRate = useCallback(() => {
        setRateChosen({ id: null, trash: null });
        loadRates();
    }, [loadRates]);

    const createRate = useCallback(() => {
        setRateChosen({ id: 'create', trash: 0 });
    }, []);

    const columns = useMemo(
        () => [
            {
                field: 'name',
                title: t('Name'),
                align: 'left' as const,
                value: (row: RateItem) => (
                    <Link
                        sx={{
                            color: 'var(--brand-text)',
                            textDecoration: 'none',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            '&:hover': { textDecoration: 'underline' },
                        }}
                        onClick={() => openRate(row)}
                    >
                        {row.name}
                    </Link>
                ),
            },
            {
                field: 'individual',
                width: 95,
                title: t('Individual'),
                type: 'active' as const,
                value: (row: RateItem) => row.individual === true,
            },
            {
                title: t('Rate'),
                align: 'center' as const,
                value: (row: RateItem) => {
                    const price = formatPriceByLocale(
                        row.prices[0]?.price,
                        cxt?.sc?.city?.country?.currency,
                    );
                    const duration = row.prices[0]?.duration || 0;
                    return (
                        <Box display="flex" justifyContent="center">
                            {`${price} / ${duration} min`}
                        </Box>
                    );
                },
            },
            {
                title: t('Groups'),
                align: 'center' as const,
                value: (row: RateItem) =>
                    row.groups?.map((item) => (
                        <Chip key={item.id} label={item.name} size="small" />
                    )),
            },
        ],
        [t, openRate, cxt?.sc?.city?.country?.currency],
    );

    return (
        <SttPage title={t('Rates')} help={startTour}>
            <SttTopControls
                p={2}
                fields={[
                    {
                        name: 'name',
                        type: 'text',
                        value: filters.name,
                        caption: t('Name'),
                        onEnterKey: loadRates,
                        xs: 12,
                        sm: 6,
                        md: 4,
                        lg: 3,
                    },
                ]}
                onChange={handleFilterChange}
                onFilter={loadRates}
                onToggleTrash={toggleTrash}
            />
            <SttTable data={rates} columns={columns} onFetch={loadRates} />

            {rateChosen.id && <Rate item={rateChosen} onClose={closeRate} />}

            <SttButtonFab
                onClick={createRate}
                permission={PERMISSIONS.RATE.CREATE}
            />
        </SttPage>
    );
}
